import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

/**
 * InfoTooltip — small gold "i" with a dark premium tooltip.
 * - Desktop: hover/focus to show.
 * - Mobile: tap to toggle; tapping outside closes.
 * - Keyboard accessible (Tab to focus, Esc to close).
 */
const InfoTooltip = ({
  label,
  ariaLabel = "More information",
  placement = "top",
  testId = "info-tooltip",
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className="ls-tooltip-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      data-testid={`${testId}-wrap`}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={`${testId}-bubble`}
        aria-expanded={open}
        className="ls-tooltip-trigger"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        data-testid={testId}
      >
        <Info size={12} strokeWidth={2.4} aria-hidden="true" />
      </button>
      <span
        id={`${testId}-bubble`}
        role="tooltip"
        className={`ls-tooltip-bubble ls-tooltip-${placement} ${open ? "open" : ""}`}
        data-testid={`${testId}-bubble`}
      >
        {label}
      </span>
    </span>
  );
};

export default InfoTooltip;
